import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  TrendingUp,
  Calendar,
  ShoppingCart,
  MessageCircle,
  Globe,
  Music,
  History,
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RevenueDetails = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(searchParams.get("period") || "month");
  const [weeksHistory, setWeeksHistory] = useState([]);
  const [monthsHistory, setMonthsHistory] = useState([]);

  useEffect(() => {
    loadData();
    loadHistory();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/revenue/details?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const [weeksRes, monthsRes] = await Promise.all([
        axios.get(`${API_URL}/api/revenue/history?type=weeks`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/revenue/history?type=months`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setWeeksHistory(weeksRes.data.history || []);
      setMonthsHistory(monthsRes.data.history || []);
    } catch (error) {
      console.error("Errore caricamento storico:", error);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setSearchParams({ period: newPeriod });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("it-IT", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "whatsapp": return <MessageCircle className="w-4 h-4" />;
      case "website": return <Globe className="w-4 h-4" />;
      case "tiktok_shop": return <Music className="w-4 h-4" />;
      default: return null;
    }
  };

  const getChannelConfig = (channel) => {
    switch (channel) {
      case "whatsapp": return { label: "WhatsApp", color: "bg-green-500 text-white" };
      case "website": return { label: "Sito Web", color: "bg-blue-500 text-white" };
      case "tiktok_shop": return { label: "TikTok", color: "bg-black text-white" };
      default: return { label: channel, color: "bg-gray-500 text-white" };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#C9A227] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="revenue-details-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Dettaglio Fatturato</h1>
            <p className="text-gray-500 text-sm">{data?.period}</p>
          </div>
        </div>

        <Select value={period} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-[160px]" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Oggi</SelectItem>
            <SelectItem value="week">Questa Settimana</SelectItem>
            <SelectItem value="month">Questo Mese</SelectItem>
          </SelectContent>
        </Select>
        
        <Link to="/revenue">
          <Button variant="outline" className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10">
            + Registra Manuale
          </Button>
        </Link>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#C9A227] to-[#D4AF37] p-6 rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/80 font-medium">Fatturato Totale</p>
              <p className="text-3xl font-bold text-white mt-1">
                {formatCurrency(data?.total_revenue)}
              </p>
            </div>
            <div className="p-3 bg-white/20 rounded-xl">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Ordini Totali</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {data?.orders_count || 0}
              </p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl">
              <ShoppingCart className="w-6 h-6 text-[#C9A227]" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Media per Ordine</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {formatCurrency((data?.total_revenue || 0) / Math.max(data?.orders_count || 1, 1))}
              </p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl">
              <Calendar className="w-6 h-6 text-[#C9A227]" />
            </div>
          </div>
        </div>
      </div>

      {/* Channel Breakdown */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-serif text-gray-900 mb-4">Fatturato per Canale</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {data?.by_channel && Object.entries(data.by_channel).map(([channel, amount]) => {
            const config = getChannelConfig(channel);
            const percentage = data.total_revenue > 0 
              ? ((amount / data.total_revenue) * 100).toFixed(1) 
              : 0;
            
            return (
              <div key={channel} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={`${config.color} px-3 py-1`}>
                    {getChannelIcon(channel)}
                    <span className="ml-2">{config.label}</span>
                  </Badge>
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(amount)}</p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${channel === 'whatsapp' ? 'bg-green-500' : channel === 'website' ? 'bg-blue-500' : 'bg-black'}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500">{percentage}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily Breakdown */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-serif text-gray-900 mb-4">Andamento Giornaliero</h2>
        <div className="space-y-3">
          {data?.daily_breakdown?.map((day) => (
            <div 
              key={day.date} 
              className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#C9A227]/10 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{formatDate(day.date)}</p>
                  <p className="text-sm text-gray-500">{day.orders} ordini</p>
                </div>
              </div>
              <p className="text-xl font-bold text-[#C9A227]">{formatCurrency(day.revenue)}</p>
            </div>
          ))}

          {(!data?.daily_breakdown || data.daily_breakdown.length === 0) && (
            <div className="text-center py-8 text-gray-400">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nessun dato disponibile per questo periodo</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-serif text-gray-900 mb-4">Ultimi Ordini</h2>
        <div className="space-y-2">
          {data?.orders?.slice(0, 10).map((order) => {
            const channelConfig = getChannelConfig(order.channel);
            return (
              <div 
                key={order.id} 
                className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Badge className={`${channelConfig.color} text-xs px-2 py-0.5`}>
                    {channelConfig.label}
                  </Badge>
                  <span className="font-medium text-gray-900">{order.customer_name}</span>
                  <span className="text-sm text-gray-400">
                    {order.items_count} {order.items_count === 1 ? "profumo" : "profumi"}
                  </span>
                </div>
                <span className="font-bold text-[#C9A227]">{formatCurrency(order.total_amount)}</span>
              </div>
            );
          })}

          {(!data?.orders || data.orders.length === 0) && (
            <div className="text-center py-8 text-gray-400">
              <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nessun ordine in questo periodo</p>
            </div>
          )}
        </div>

        {data?.orders?.length > 10 && (
          <div className="mt-4 text-center">
            <Link to="/sales">
              <Button variant="outline" className="text-[#C9A227] border-[#C9A227] hover:bg-amber-50">
                Vedi tutti gli ordini
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Historical Data - Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly History */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-[#C9A227]" />
            <h2 className="text-lg font-serif text-gray-900">Storico Settimanale</h2>
          </div>
          <div className="space-y-3">
            {weeksHistory.slice(0, 8).map((week) => (
              <div 
                key={week.key} 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">{week.label}</p>
                  <p className="text-xs text-gray-500">{week.orders} ordini</p>
                </div>
                <p className="text-lg font-bold text-[#C9A227]">{formatCurrency(week.revenue)}</p>
              </div>
            ))}
            {weeksHistory.length === 0 && (
              <p className="text-center text-gray-400 py-4">Nessun dato disponibile</p>
            )}
          </div>
        </div>

        {/* Monthly History */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-[#C9A227]" />
            <h2 className="text-lg font-serif text-gray-900">Storico Mensile</h2>
          </div>
          <div className="space-y-3">
            {monthsHistory.slice(0, 8).map((month) => (
              <div 
                key={month.key} 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">{month.label}</p>
                  <div className="flex gap-2 mt-1">
                    {month.by_channel.website > 0 && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        Web: {formatCurrency(month.by_channel.website)}
                      </span>
                    )}
                    {month.by_channel.whatsapp > 0 && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        WA: {formatCurrency(month.by_channel.whatsapp)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[#C9A227]">{formatCurrency(month.revenue)}</p>
                  <p className="text-xs text-gray-500">{month.orders} ordini</p>
                </div>
              </div>
            ))}
            {monthsHistory.length === 0 && (
              <p className="text-center text-gray-400 py-4">Nessun dato disponibile</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueDetails;
